import React from 'react';
import ReactMarkdown, { defaultUrlTransform, type UrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MarkdownFigure, MarkdownFigcaption, MarkdownImg } from './Chat.styled';
import { artifactDownloadPath, fetchArtifactName, isArtifactDownloadUrl } from '@utils/authDownload';
import useAuthDownload, { type DownloadFilename } from '@hooks/useAuthDownload';

const REMARK_PLUGINS = [remarkGfm];
const URL_SCHEME_PATTERN = /^[a-z][a-z\d+.-]*:/i;
const FILE_EXTENSION_PATTERN = /\.[a-z\d]{1,10}$/i;
const MIDDLE_MOUSE_BUTTON = 1;

// Only links: an image src the browser fetches itself cannot carry the auth an artifact needs.
const urlTransform: UrlTransform = (url, key) =>
  (key === 'href' && artifactDownloadPath(url)) || defaultUrlTransform(url);

const MarkdownImage: React.FC<React.ComponentPropsWithoutRef<'img'>> = ({ src, alt, ...rest }) => {
  if (!src) return null;

  return (
    <MarkdownFigure>
      <MarkdownImg src={src} alt={alt || 'image'} {...rest} />
      {alt && <MarkdownFigcaption>{alt}</MarkdownFigcaption>}
    </MarkdownFigure>
  );
};

const filenameFromUrl = (href: string): string => {
  const segments = href.split('/');
  return segments.at(-1) || 'download';
};

const extractPlainText = (children: React.ReactNode): string => {
  const parts = React.Children.toArray(children);
  return parts
    .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : ''))
    .join('')
    .trim();
};

const downloadFilename = (href: string, text: string): DownloadFilename => {
  if (FILE_EXTENSION_PATTERN.test(text)) return text;
  return async () => (await fetchArtifactName(href)) ?? (text || filenameFromUrl(href));
};

const isPageRelativeHref = (href: string): boolean =>
  !URL_SCHEME_PATTERN.test(href) && !href.startsWith('/');

const ArtifactMarkdownLink: React.FC<React.ComponentPropsWithoutRef<'a'> & { href: string }> = ({
  href,
  children,
  ...rest
}) => {
  const { download } = useAuthDownload();

  const handleActivate = (e: React.SyntheticEvent) => {
    e.preventDefault();
    void download(href, downloadFilename(href, extractPlainText(children)));
  };

  const handleAuxClick = (e: React.MouseEvent) => {
    if (e.button === MIDDLE_MOUSE_BUTTON) handleActivate(e);
  };

  return (
    <a href={href} onClick={handleActivate} onAuxClick={handleAuxClick} {...rest}>
      {children}
    </a>
  );
};

const MarkdownLink: React.FC<React.ComponentPropsWithoutRef<'a'>> = ({
  href,
  children,
  ...rest
}) => {
  if (!href || isPageRelativeHref(href)) return <>{children}</>;

  if (isArtifactDownloadUrl(href)) {
    return (
      <ArtifactMarkdownLink href={href} {...rest}>
        {children}
      </ArtifactMarkdownLink>
    );
  }

  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
      {children}
    </a>
  );
};

/** Prevent react-markdown internal props (node, etc.) from leaking to DOM */
const mdComponents = {
  p: ({ children }: { children?: React.ReactNode }) => {
    const childArray = React.Children.toArray(children);
    if (
      childArray.length === 1 &&
      React.isValidElement(childArray[0]) &&
      childArray[0].type === MarkdownImage
    ) {
      return <>{children}</>;
    }
    return <p>{children}</p>;
  },
  h1: ({ children }: { children?: React.ReactNode }) => <h1>{children}</h1>,
  h2: ({ children }: { children?: React.ReactNode }) => <h2>{children}</h2>,
  h3: ({ children }: { children?: React.ReactNode }) => <h3>{children}</h3>,
  h4: ({ children }: { children?: React.ReactNode }) => <h4>{children}</h4>,
  h5: ({ children }: { children?: React.ReactNode }) => <h5>{children}</h5>,
  h6: ({ children }: { children?: React.ReactNode }) => <h6>{children}</h6>,
  ul: ({ children }: { children?: React.ReactNode }) => <ul>{children}</ul>,
  ol: ({ children }: { children?: React.ReactNode }) => <ol>{children}</ol>,
  li: ({ children }: { children?: React.ReactNode }) => <li>{children}</li>,
  img: MarkdownImage,
  a: MarkdownLink,
};

const Markdown: React.FC<{ children: string }> = ({ children }) => (
  <ReactMarkdown remarkPlugins={REMARK_PLUGINS} components={mdComponents} urlTransform={urlTransform}>
    {children}
  </ReactMarkdown>
);

export default Markdown;
